import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import InventoryIcon from '@mui/icons-material/Inventory';
import PeopleIcon from '@mui/icons-material/People';
import ApartmentIcon from '@mui/icons-material/Apartment';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import AssignmentIcon from '@mui/icons-material/Assignment';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import CategoryIcon from '@mui/icons-material/Category';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import GroupIcon from '@mui/icons-material/Group';
import BarChartIcon from '@mui/icons-material/BarChart';
import PaidIcon from '@mui/icons-material/Paid';
import SettingsIcon from '@mui/icons-material/Settings';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { clearActivityPage } from '../../utils/activityFiltersStorage';
import { companyService, type Company } from '../../services/companyService';
import { ModuleGrid, type ModuleCard } from '../common/ModuleGrid';
import {
  FULL_ROW,
  HALF_ROW,
} from '../common/moduleCardLayouts';

export default function CompanyHomePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters survive a visit to the home screen, but the next visit to activities starts from the first page
  useEffect(() => {
    if (id) {
      clearActivityPage(parseInt(id));
    }
  }, [id]);

  useEffect(() => {
    let cancelled = false;

    const fetchCompany = async () => {
      if (!id) {
        setError('Ідентифікатор компанії відсутній');
        setLoading(false);
        return;
      }

      try {
        const companyId = parseInt(id);
        const companyData = await companyService.getById(companyId);
        if (cancelled) {
          return;
        }
        setCompany(companyData);
      } catch (err) {
        if (cancelled) {
          return;
        }
        console.error('Error fetching company:', err);
        setError('Не вдалося завантажити дані компанії');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchCompany();

    return () => {
      cancelled = true;
    };
  }, [id]);

  const modules: ModuleCard[] = [
    {
      id: 'activities',
      title: 'Активності',
      description: 'Облік постачань, продажів та переміщень',
      icon: AssignmentIcon,
      path: `/companies/${id}/activities`,
      layout: FULL_ROW,
    },
    {
      id: 'products',
      title: 'Продукти',
      description: 'Управління каталогом продуктів',
      icon: InventoryIcon,
      path: `/companies/${id}/products`,
      layout: HALF_ROW,
    },
    {
      id: 'contacts',
      title: 'Контакти',
      description: 'Управління клієнтами та постачальниками',
      icon: PeopleIcon,
      path: `/companies/${id}/contacts`,
      layout: HALF_ROW,
    },
    {
      id: 'catalogs',
      title: 'Каталоги',
      description: 'Товари з цінами для передзамовлень клієнтів',
      icon: MenuBookIcon,
      path: `/companies/${id}/catalogs`,
      layout: HALF_ROW,
    },
    {
      id: 'preorders',
      title: 'Передзамовлення',
      description: 'Замовлення клієнтів з каталогів',
      icon: ShoppingCartIcon,
      path: `/companies/${id}/preorders`,
      layout: HALF_ROW,
    },
    {
      id: 'organizations',
      title: 'Організації',
      description: 'Компанії-контрагенти, до яких належать контакти',
      icon: ApartmentIcon,
      path: `/companies/${id}/organizations`,
      layout: HALF_ROW,
    },
    {
      id: 'stocks',
      title: 'Склади',
      description: 'Облік залишків та рівня запасів',
      icon: WarehouseIcon,
      path: `/companies/${id}/stocks`,
      layout: HALF_ROW,
    },
    {
      id: 'stock-audits',
      title: 'Ревізія',
      description: 'Звірка складу зі сканером серійників',
      icon: FactCheckIcon,
      path: `/companies/${id}/stock-audits`,
      layout: HALF_ROW,
    },
    {
      id: 'activity-types',
      title: 'Типи активностей',
      description: 'Визначення типів операцій (НАДХОДЖЕННЯ/СПИСАННЯ)',
      icon: CategoryIcon,
      path: `/companies/${id}/activity-types`,
      layout: HALF_ROW,
    },
    {
      id: 'team',
      title: 'Команда',
      description: 'Учасники та ролі',
      icon: GroupIcon,
      path: `/companies/${id}/team`,
      layout: HALF_ROW,
    },
    {
      id: 'reports',
      title: 'Звіти',
      description: 'Рух коштів та інша статистика',
      icon: BarChartIcon,
      path: `/companies/${id}/reports`,
      layout: HALF_ROW,
    },
    {
      id: 'debts',
      title: 'Борги',
      description: 'Хто винен нам, і кому винні ми',
      icon: PaidIcon,
      path: `/companies/${id}/debts`,
      layout: HALF_ROW,
    },
    {
      id: 'settings',
      title: 'Налаштування',
      description: 'Доставка та інша інтеграція',
      icon: SettingsIcon,
      path: `/companies/${id}/settings`,
      layout: HALF_ROW,
    },
  ];

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '80vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !company) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {error || 'Компанію не знайдено'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 10,
          backgroundColor: 'background.default',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          py: 1,
          mb: 1.5,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/companies')}
          size="small"
        >
          До компаній
        </Button>
        <Typography
          variant="button"
          component="h1"
          noWrap
          sx={{
            minWidth: 0,
            pr: { xs: '9px', sm: '10px' },
            color: 'primary.main',
          }}
        >
          {company.name}
        </Typography>
      </Box>

      <ModuleGrid modules={modules} />
    </Box>
  );
}
